import {t} from '@lingui/core/macro';
import {createSelector, createSlice, PayloadAction} from '@reduxjs/toolkit';

import {RootState} from 'app/store';

import IUserOutput from './IUser';
import {Permission} from './Permission';
import {oauthInit} from './thunks/oauthInit';
import {oauthFinish} from './thunks/oauthFinish';
import {registerUser} from './thunks/registerUser';
import {loginUser} from './thunks/loginUser';
import {impersonate} from './thunks/impersonate';
import {fetchMe} from './thunks/fetchMe';
import {confirmEmail} from './thunks/confirmEmail';
import {confirmChangeEmail} from './thunks/confirmChangeEmail';
import {passwordResetRequest} from './thunks/passwordResetRequest';
import {setNewPassword} from './thunks/setNewPassword';
import {editUser} from './thunks/editUser';
import {disableRecurring} from './thunks/disableRecurring';

interface IInitialState {
    registerUserLoading: boolean,
    registerUserSuccess: boolean,

    loginLoading: boolean,
    isAuthenticated: boolean,
    isFetchMeError: boolean,
    user: IUserOutput | null,
    isUserLoading: boolean,

    isEmailConfirming: boolean,
    isPasswordResetting: boolean,
    isPasswordSetting: boolean,
    isUserEditing: boolean,

    lastNewsletterModalShown: number | null;

    oauthLoading: boolean;
    oauthError: string | null;
}

const initialState: IInitialState = {
    registerUserLoading: false,
    registerUserSuccess: false,

    loginLoading: false,
    isAuthenticated: false,
    isFetchMeError: false,
    user: null,
    isUserLoading: false,

    isEmailConfirming: false,
    isPasswordResetting: false,
    isPasswordSetting: false,
    isUserEditing: false,

    lastNewsletterModalShown: null,

    oauthLoading: false,
    oauthError: null,
};

const auth = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        logoutAction: (state) => {
            state.user = null;
            state.isAuthenticated = false;
        },
        setLastNewsletterModalShown: (state, action: PayloadAction<number | null>) => {
            state.lastNewsletterModalShown = action.payload;

            if (action.payload === null) {
                localStorage.removeItem('lastNewsletterModalShown');
            } else {
                localStorage.setItem('lastNewsletterModalShown', action.payload.toString());
            }
        },
        setUserEmailActive: (state, action: PayloadAction<boolean>) => {
            if (state.user) {
                state.user.email_active = action.payload;
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(registerUser.pending, (state) => {
            state.registerUserLoading = true;
        });
        builder.addCase(registerUser.fulfilled, (state) => {
            state.registerUserLoading = false;
            state.registerUserSuccess = true;
        });
        builder.addCase(registerUser.rejected, (state) => {
            state.registerUserLoading = false;
        });

        builder.addCase(editUser.pending, (state) => {
            state.isUserEditing = true;
        });
        builder.addCase(editUser.fulfilled, (state, {payload}) => {
            state.user = payload;
            state.isUserEditing = false;
        });
        builder.addCase(editUser.rejected, (state) => {
            state.isUserEditing = false;
        });

        builder.addCase(loginUser.pending, (state) => {
            state.loginLoading = true;
        });
        builder.addCase(loginUser.fulfilled, (state) => {
            state.loginLoading = false;
            state.isAuthenticated = true;
        });
        builder.addCase(loginUser.rejected, (state) => {
            state.loginLoading = false;
        });

        builder.addCase(impersonate.pending, (state) => {
            state.loginLoading = true;
        });
        builder.addCase(impersonate.fulfilled, (state) => {
            state.loginLoading = false;
            state.isAuthenticated = true;
        });
        builder.addCase(impersonate.rejected, (state) => {
            state.loginLoading = false;
        });

        builder.addCase(confirmEmail.pending, (state) => {
            state.isEmailConfirming = true;
        });
        builder.addCase(confirmEmail.fulfilled, (state) => {
            state.isEmailConfirming = false;
        });
        builder.addCase(confirmEmail.rejected, (state) => {
            state.isEmailConfirming = false;
        });

        builder.addCase(confirmChangeEmail.pending, (state) => {
            state.isEmailConfirming = true;
        });
        builder.addCase(confirmChangeEmail.fulfilled, (state, {payload}) => {
            state.isEmailConfirming = false;
            if (state.user !== null) {
                state.user.email = payload.email;
            }
        });
        builder.addCase(confirmChangeEmail.rejected, (state) => {
            state.isEmailConfirming = false;
        });

        builder.addCase(passwordResetRequest.pending, (state) => {
            state.isPasswordResetting = true;
        });
        builder.addCase(passwordResetRequest.fulfilled, (state) => {
            state.isPasswordResetting = false;
        });
        builder.addCase(passwordResetRequest.rejected, (state) => {
            state.isPasswordResetting = false;
        });

        builder.addCase(setNewPassword.pending, (state) => {
            state.isPasswordSetting = true;
        });
        builder.addCase(setNewPassword.fulfilled, (state) => {
            state.isPasswordSetting = false;
        });
        builder.addCase(setNewPassword.rejected, (state) => {
            state.isPasswordSetting = false;
        });

        builder.addCase(fetchMe.pending, (state, {meta}) => {
            if (!meta.arg) {
                state.isUserLoading = true;
            }
        });
        builder.addCase(fetchMe.fulfilled, (state, {payload, meta}) => {
            if (JSON.stringify(state.user) !== JSON.stringify(payload)) {
                state.user = payload || null;
            }
            if (!meta.arg) {
                state.isUserLoading = false;
            }
            state.isFetchMeError = false;
        });
        builder.addCase(fetchMe.rejected, (state, {meta}) => {
            if (!meta.arg) {
                state.isUserLoading = false;
            }
            state.isFetchMeError = true;
            state.user = null;
        });

        builder.addCase(disableRecurring.pending, (state) => {
            state.isUserLoading = true;
        });
        builder.addCase(disableRecurring.fulfilled, (state) => {
            state.isUserLoading = false;
        });
        builder.addCase(disableRecurring.rejected, (state) => {
            state.isUserLoading = false;
        });

        builder.addCase(oauthInit.pending, (state) => {
            state.oauthLoading = true;
            state.oauthError = null;
        });
        builder.addCase(oauthInit.fulfilled, (state) => {
            state.oauthLoading = false;
        });
        builder.addCase(oauthInit.rejected, (state, action) => {
            state.oauthLoading = false;
            state.oauthError = action.error.message || t`Ошибка инициализации OAuth`;
        });

        builder.addCase(oauthFinish.pending, (state) => {
            state.oauthLoading = true;
            state.oauthError = null;
        });
        builder.addCase(oauthFinish.fulfilled, (state) => {
            state.oauthLoading = false;
            state.isAuthenticated = true;
        });
        builder.addCase(oauthFinish.rejected, (state, action) => {
            state.oauthLoading = false;
            state.oauthError = action.error.message || t`Ошибка завершения OAuth`;
        });
    },
});

export default auth.reducer;
export const {logoutAction, setLastNewsletterModalShown, setUserEmailActive} = auth.actions;

//Селекторы
const slice = ({authReducer}: RootState) => authReducer;

export const registerUserLoadingSelector = createSelector(
    slice,
    ({registerUserLoading}) => registerUserLoading,
);

export const registerUserSuccessSelector = createSelector(
    slice,
    ({registerUserSuccess}) => registerUserSuccess,
);

export const authLoadingSelector = createSelector(
    slice,
    ({loginLoading}) => loginLoading,
);

export const isAuthenticatedSelector = createSelector(
    slice,
    ({isAuthenticated, user}) => isAuthenticated || !!user,
);

export const userSelector = createSelector(
    slice,
    ({user}) => user,
);

export const userPermissionsSelector = createSelector(
    userSelector,
    (user) => {
        const permissions: Permission[] = [];
        if (!user) {
            return permissions;
        }

        return user.roles;
    },
);

export const isUserLoadingSelector = createSelector(
    slice,
    ({isUserLoading}) => isUserLoading,
);

export const isEmailConfirmingSelector = createSelector(
    slice,
    ({isEmailConfirming}) => isEmailConfirming,
);

export const isPasswordResettingSelector = createSelector(
    slice,
    ({isPasswordResetting}) => isPasswordResetting,
);

export const isPasswordSettingSelector = createSelector(
    slice,
    ({isPasswordSetting}) => isPasswordSetting,
);

export const isUserEditingSelector = createSelector(
    slice,
    ({isUserEditing}) => isUserEditing,
);

export const isFetchMeErrorSelector = createSelector(
    slice,
    ({isFetchMeError}) => isFetchMeError,
);

export const lastNewsletterModalShownSelector = createSelector(
    slice,
    ({lastNewsletterModalShown}) => lastNewsletterModalShown,
);